import type { MasterFieldDef, MasterSchema } from '../constants/masters';
import type { MasterFormState } from '../components/masters/MasterFormDrawer';
import { TIME_PATTERN } from '../constants/foodMasterImageSpecs';
import { COMMON_LETTER_ONLY_NAME_TYPES } from '../constants/commonManagedMasters';
import { TRANSPORT_LETTER_ONLY_NAME_TYPES, TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES, TRANSPORT_INTEGER_SORT_ORDER_TYPES, TRANSPORT_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_DESCRIPTION_VALIDATED_TYPES, filterRecordsForParentScopedSortOrder, validateIntegerSortOrder, validatePassengerCapacity, validateVehicleServiceTypes, validateRideTagColor, parseCommaSeparatedDayValues } from '../constants/transportSubserviceMasters';
import { FOOD_SEQUENTIAL_SORT_ORDER_TYPES, FOOD_INTEGER_SORT_ORDER_TYPES, FOOD_UNIQUE_SORT_ORDER_TYPES, FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, FOOD_LETTER_ONLY_NAME_TYPES, FOOD_DESCRIPTION_VALIDATED_TYPES, FOOD_AUTO_CODE_TYPES, isValidFoodMasterCode, validateRestaurantBranchType, validateDuplicateFoodSubCategoryName, validateDuplicateFoodSubCategoryCode, validateDuplicatePreparationMinutes, validatePreparationTimeName, preparationTimeCodeFromMinutes, validateFoodUnitType, validateFoodTaxPercent, formatFoodTaxGroupName, validateKitchenStationType, validateFoodAvailabilityScheduleName, resolveRecordSortOrder } from '../constants/foodMasterRules';
import { DELIVERY_INTEGER_SORT_ORDER_TYPES, DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES, DELIVERY_UNIQUE_SORT_ORDER_TYPES, DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, DELIVERY_AUTO_CODE_TYPES, DELIVERY_DESCRIPTION_VALIDATED_TYPES, validateDeliverySlotFee, validatePackagingFee, validateDeliveryPartnerPricingName, validateDeliveryPartnerPricingDescription, validateDeliveryPartnerPricingFields, validateDuplicateDeliveryPartnerPricingZoneService, validateDeliveryIncentiveAmount, validateDeliveryIncentivePeakTimeRange, validateDeliveryPartnerTierMinOrders, validateDeliveryPartnerTierBonusPercent, validateDuplicateDeliveryPartnerTierMinOrders, validateDeliveryPartnerTierSequence, validateMerchantDocumentFields, validateCancellationReasonFields, validateSubstitutionRuleFields, validateStockAlertThreshold, validateGroceryProductImageFields, validateGroceryAvailabilityScheduleFields } from '../constants/deliveryMasterRules';
import {
  ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES,
  ON_DEMAND_INTEGER_SORT_ORDER_TYPES,
  ON_DEMAND_UNIQUE_SORT_ORDER_TYPES,
  ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES,
  ON_DEMAND_DESCRIPTION_VALIDATED_TYPES,
} from '../constants/onDemandMasters';
import { validateDuplicateOpeningHourDay, isOpeningHourClosed } from './restaurantOpeningHours';
import { normalizeMetadataFields } from '../constants/masterRefFields';
import { validatePersonName, validatePhoneNumber, validateLocalPhoneForCountry, validateEmail } from './personFieldValidation';
import { phoneLengthForDialCode } from '../services/countryDialCodeService';
import { localPhoneDigits } from './phoneDialCode';
import { validateOptionalDescription } from './descriptionValidation';

export type FieldErrors = Record<string, string>;

function fieldErrorKey(key: string, isMeta: boolean) {
  return isMeta ? `meta:${key}` : key;
}

function validateField(
  field: MasterFieldDef,
  value: string | number | boolean | undefined,
  isMeta: boolean,
  errors: FieldErrors,
) {
  const key = fieldErrorKey(field.key, isMeta);
  const str = value === undefined || value === null ? '' : String(value);
  const isEmpty = str.trim() === '' && typeof value !== 'boolean' && typeof value !== 'number';

  if (field.required && isEmpty) {
    errors[key] = `${field.label} is required`;
    return;
  }
  if (isEmpty) return;

  if (field.type === 'number' || field.type === 'currency' || field.type === 'percent') {
    const num = Number(value);
    if (Number.isNaN(num)) {
      errors[key] = `${field.label} must be a number`;
      return;
    }
    if (field.key === 'capacity' && !Number.isInteger(num)) {
      errors[key] = `${field.label} must be a whole number (no decimals)`;
      return;
    }
    if (field.min !== undefined && num < field.min) {
      errors[key] = `${field.label} must be at least ${field.min}`;
    }
    if (field.max !== undefined && num > field.max) {
      errors[key] = `${field.label} must be at most ${field.max}`;
    }
    if (field.type === 'percent' && (num < 0 || num > 100)) {
      errors[key] = `${field.label} must be between 0 and 100`;
    }
  }

  if (field.pattern) {
    const re = new RegExp(field.pattern);
    if (!re.test(str)) {
      errors[key] = field.patternMessage || `${field.label} format is invalid`;
    }
  }

  if (field.key === 'openTime' || field.key === 'closeTime' || field.key === 'startTime' || field.key === 'endTime' || field.key === 'start' || field.key === 'end' || field.type === 'time') {
    if (!TIME_PATTERN.test(str)) {
      errors[key] = 'Use 24-hour format HH:mm (e.g. 10:00, 22:30)';
    }
  }

  if (field.key === 'phone') {
    const phoneErr = validatePhoneNumber(str, field.label || 'Phone', { required: field.required });
    if (phoneErr) errors[key] = phoneErr;
    return;
  }
}

export function validateMasterForm(
  form: MasterFormState,
  schema: MasterSchema | null | undefined,
  opts?: {
    hasParent?: boolean;
    requiredSortOrder?: number;
    existingRecords?: Array<{ _id?: string; code?: string; sortOrder?: number; parentId?: string; attributeCode?: string; metadata?: Record<string, unknown> }>;
    editingId?: string;
    masterType?: string;
    sortOrderParentId?: string;
    sortOrderAttributeCode?: string;
    sortOrderParentCategoryCode?: string;
    branchRecords?: Array<{ _id?: string; metadata?: Record<string, unknown> }>;
    openingHourRecords?: Array<{ _id?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>;
    validRefCodes?: Partial<Record<string, string[]>>;
  },
): FieldErrors {
  const errors: FieldErrors = {};
  const masterType = schema?.type ?? opts?.masterType;

  if (!form.code?.trim()) errors.code = 'Code is required';
  else if (masterType && (FOOD_AUTO_CODE_TYPES.has(masterType) || DELIVERY_AUTO_CODE_TYPES.has(masterType)) && !isValidFoodMasterCode(form.code)) {
    errors.code = 'Code must use lowercase letters, numbers, and underscores (e.g. lunch_slot)';
  } else if (!/^[A-Za-z0-9_]+$/.test(form.code.trim())) {
    errors.code = 'Code may only contain letters, numbers, and underscores';
  } else if (opts?.existingRecords?.length) {
    const duplicateErr = validateDuplicateMasterCode(
      form.code,
      opts.existingRecords,
      opts.editingId,
    );
    if (duplicateErr) errors.code = duplicateErr;
  }

  if (masterType === 'food_attribute_values') {
    const attributeCode = String(form.metadata.attributeCode ?? '').trim();
    if (!attributeCode && !opts?.sortOrderParentId) {
      errors['meta:attributeCode'] = 'Parent Attribute is required';
    }
  }

  if (masterType === 'food_sub_categories') {
    const parentCode = String(form.metadata.parentCategoryCode ?? '').trim();
    if (!parentCode) {
      errors['meta:parentCategoryCode'] = 'Parent category is required';
    }
    const subRecords = (opts?.existingRecords || []) as Array<{ _id?: string; code?: string; name?: string; metadata?: Record<string, unknown> }>;
    if (parentCode) {
      const scopedCodeErr = validateDuplicateFoodSubCategoryCode(form.code, parentCode, subRecords, opts?.editingId);
      if (scopedCodeErr) errors.code = scopedCodeErr;
      const nameDupErr = validateDuplicateFoodSubCategoryName(form.name, parentCode, subRecords, opts?.editingId);
      if (nameDupErr) errors.name = nameDupErr;
    }
  }

  if (masterType && (TRANSPORT_LETTER_ONLY_NAME_TYPES.has(masterType) || FOOD_LETTER_ONLY_NAME_TYPES.has(masterType) || COMMON_LETTER_ONLY_NAME_TYPES.has(masterType))) {
    const nameErr = validatePersonName(form.name, 'Display name', { required: true });
    if (nameErr) errors.name = nameErr;
  } else if (masterType === 'delivery_partner_pricing') {
    const nameErr = validateDeliveryPartnerPricingName(form.name);
    if (nameErr) errors.name = nameErr;
  } else if (!form.name?.trim()) {
    errors.name = 'Display name is required';
  }

  const usesIntegerSortOrder = masterType && (
    TRANSPORT_INTEGER_SORT_ORDER_TYPES.has(masterType)
    || FOOD_INTEGER_SORT_ORDER_TYPES.has(masterType)
    || DELIVERY_INTEGER_SORT_ORDER_TYPES.has(masterType)
    || ON_DEMAND_INTEGER_SORT_ORDER_TYPES.has(masterType)
  );
  const usesSequentialSortOrder = masterType && (
    TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(masterType)
    || FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(masterType)
    || DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(masterType)
    || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(masterType)
  );
  const usesUniqueSortOrder = masterType && (
    TRANSPORT_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || FOOD_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || DELIVERY_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || ON_DEMAND_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    || ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)
  );

  if (usesIntegerSortOrder) {
    if (
      (masterType === 'food_sub_categories' || masterType === 'grocery_sub_categories')
      && !String(form.metadata.parentCategoryCode ?? '').trim()
    ) {
      errors.sortOrder = 'Select a parent category first — sort order is set automatically';
    } else if (masterType === 'food_attribute_values' && !opts?.sortOrderAttributeCode && !opts?.sortOrderParentId) {
      errors.sortOrder = 'Select a parent attribute first — sort order is set automatically';
    } else {
    const sortErr = validateIntegerSortOrder(form.sortOrder);
    if (sortErr) {
      errors.sortOrder = sortErr;
    } else if (
      usesSequentialSortOrder
      && opts?.requiredSortOrder != null
      && form.sortOrder !== opts.requiredSortOrder
    ) {
      errors.sortOrder = `Sort order must be ${opts.requiredSortOrder} — the next number in sequence`;
    } else if (usesUniqueSortOrder) {
      const scopedRecords = filterRecordsForParentScopedSortOrder(
        masterType,
        opts?.existingRecords || [],
        {
          parentId: opts?.sortOrderParentId,
          attributeCode: opts?.sortOrderAttributeCode,
          parentCategoryCode: opts?.sortOrderParentCategoryCode,
        },
      );
      const duplicateErr = validateDuplicateSortOrder(
        form.sortOrder,
        scopedRecords,
        opts?.editingId,
      );
      if (duplicateErr) errors.sortOrder = duplicateErr;
    }
    }
  }

  if (masterType && (
    TRANSPORT_DESCRIPTION_VALIDATED_TYPES.has(masterType)
    || FOOD_DESCRIPTION_VALIDATED_TYPES.has(masterType)
    || DELIVERY_DESCRIPTION_VALIDATED_TYPES.has(masterType)
    || ON_DEMAND_DESCRIPTION_VALIDATED_TYPES.has(masterType)
  )) {
    if (masterType === 'delivery_partner_pricing') {
      const descriptionErr = validateDeliveryPartnerPricingDescription(form.description);
      if (descriptionErr) errors.description = descriptionErr;
    } else {
      const descriptionErr = validateOptionalDescription(form.description);
      if (descriptionErr) errors.description = descriptionErr;
    }
  }

  if (
    opts?.hasParent
    && !form.parentId
    && masterType !== 'food_sub_categories'
    && masterType !== 'food_attribute_values'
  ) {
    errors.parentId = 'Parent master is required';
  }

  if (masterType === 'warehouses' && !String(form.country ?? '').trim()) {
    errors.country = 'Country is required';
  }

  for (const field of normalizeMetadataFields(schema?.metadataFields)) {
    if (
      schema?.type === 'restaurant_opening_hours'
      && isOpeningHourClosed(form.metadata)
      && (field.key === 'openTime' || field.key === 'closeTime')
    ) {
      continue;
    }
    if (
      (schema?.type === 'delivery_incentives' || masterType === 'delivery_incentives')
      && (field.key === 'startTime' || field.key === 'endTime' || field.key === 'start' || field.key === 'end')
      && String(form.metadata.incentiveType ?? '').trim().toLowerCase() !== 'peak_hour'
    ) {
      continue;
    }
    if ((masterType === 'suppliers' || schema?.type === 'suppliers') && field.key === 'phone') {
      continue;
    }
    validateField(field, form.metadata[field.key], true, errors);
  }

  if (masterType === 'suppliers' || schema?.type === 'suppliers') {
    const phoneLocal = localPhoneDigits(String(form.metadata.phone ?? ''));
    const dial = String(form.metadata.phoneCountryCode ?? '').trim();
    if (phoneLocal) {
      if (!dial) {
        errors['meta:phone'] = 'Select a country code before entering the phone number';
      } else {
        const phoneErr = validateLocalPhoneForCountry(
          phoneLocal,
          phoneLengthForDialCode(dial),
          'Phone',
          { dialCode: dial },
        );
        if (phoneErr) errors['meta:phone'] = phoneErr;
      }
    }
    const email = String(form.metadata.email ?? '').trim();
    if (email) {
      const emailErr = validateEmail(email, 'Email');
      if (emailErr) errors['meta:email'] = emailErr;
    }
  }

  if (masterType === 'stock_alerts' || schema?.type === 'stock_alerts') {
    const thresholdErr = validateStockAlertThreshold(form.metadata.threshold);
    if (thresholdErr) errors['meta:threshold'] = thresholdErr;
  }

  if (masterType === 'grocery_product_images' || schema?.type === 'grocery_product_images') {
    const imageErrors = validateGroceryProductImageFields(
      form.metadata as Record<string, unknown>,
      opts?.existingRecords || [],
      opts?.editingId,
    );
    for (const [key, message] of Object.entries(imageErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (masterType === 'grocery_availability_schedules' || schema?.type === 'grocery_availability_schedules') {
    const scheduleErrors = validateGroceryAvailabilityScheduleFields(
      form.metadata as Record<string, unknown>,
    );
    for (const [key, message] of Object.entries(scheduleErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'vehicle_types') {
    const capacityErr = validatePassengerCapacity(form.metadata.capacity, 'Passenger Capacity', { required: true });
    if (capacityErr) errors['meta:capacity'] = capacityErr;

    const serviceTypesErr = validateVehicleServiceTypes(form.metadata.serviceTypes);
    if (serviceTypesErr) errors['meta:serviceTypes'] = serviceTypesErr;
  }

  if (schema?.type === 'ride_tags' || schema?.type === 'food_tags') {
    const colorErr = validateRideTagColor(form.metadata.color, 'Tag Color', { required: schema?.type === 'food_tags' });
    if (colorErr) errors['meta:color'] = colorErr;
  }

  const meta = form.metadata;
  if (schema?.type === 'food_addons') {
    const groupCode = String(meta.addonGroupCode ?? '').trim();
    const validGroups = opts?.validRefCodes?.addon_groups;
    if (groupCode && validGroups?.length && !validGroups.some((code) => code.toLowerCase() === groupCode.toLowerCase())) {
      errors['meta:addonGroupCode'] = 'Select a valid addon group from the list';
    }
  }

  if (schema?.type === 'payout_cycles') {
    const rawDays = String(meta.days ?? '').trim();
    const days = Number(rawDays);
    if (!rawDays) {
      errors['meta:days'] = 'Cycle days is required — how often partners are paid';
    } else if (!Number.isInteger(days) || days < 1 || days > 90) {
      errors['meta:days'] = 'Cycle days must be a whole number from 1 to 90';
    } else {
      const clash = (opts?.existingRecords || []).find(
        (r) => r._id !== opts?.editingId && Number(r.metadata?.days) === days,
      ) as { name?: string } | undefined;
      if (clash) errors['meta:days'] = `${clash.name ? `"${clash.name}"` : 'Another cycle'} already pays every ${days} day${days === 1 ? '' : 's'}`;
    }
  }

  if (schema?.type === 'preparation_times') {
    const minutes = Number(meta.minutes);
    if (!Number.isInteger(minutes) || minutes < 1) {
      errors['meta:minutes'] = 'Minutes must be a whole number from 1 to 180';
    } else if (minutes > 180) {
      errors['meta:minutes'] = 'Minutes must be at most 180';
    } else {
      const minutesDupErr = validateDuplicatePreparationMinutes(
        minutes,
        opts?.existingRecords || [],
        opts?.editingId,
      );
      if (minutesDupErr) errors['meta:minutes'] = minutesDupErr;
      const nameErr = validatePreparationTimeName(form.name, minutes);
      if (nameErr) errors.name = nameErr;
      if (!opts?.editingId) {
        const expectedCode = preparationTimeCodeFromMinutes(minutes);
        if (expectedCode && form.code.trim().toLowerCase() !== expectedCode) {
          errors.code = `Code must be "${expectedCode}" for ${minutes} minutes`;
        }
      }
    }
  }

  if (schema?.type === 'food_units') {
    const unitTypeErr = validateFoodUnitType(meta.unitType);
    if (unitTypeErr) errors['meta:unitType'] = unitTypeErr;
  }

  if (schema?.type === 'food_tax_groups' || schema?.type === 'grocery_tax_groups' || masterType === 'grocery_tax_groups') {
    const taxErr = validateFoodTaxPercent(meta.taxPercent);
    if (taxErr) errors['meta:taxPercent'] = taxErr;
    const taxPercent = Number(meta.taxPercent);
    if (Number.isFinite(taxPercent)) {
      const expectedName = formatFoodTaxGroupName(taxPercent);
      if (expectedName && String(form.name ?? '').trim() !== expectedName) {
        errors.name = `Display name must be "${expectedName}" — update Tax % to refresh the name`;
      }
    }
  }

  if (schema?.type === 'kitchen_stations') {
    const stationTypeErr = validateKitchenStationType(meta.stationType);
    if (stationTypeErr) errors['meta:stationType'] = stationTypeErr;
  }

  if (schema?.type === 'addon_groups') {
    const minRaw = meta.minSelection;
    const maxRaw = meta.maxSelection;
    const min = Number(minRaw);
    const max = Number(maxRaw);
    const required = meta.required === true || meta.required === 'true';

    if (minRaw === '' || minRaw == null || !Number.isInteger(min) || min < 0) {
      errors['meta:minSelection'] = 'Min selection must be a whole number ≥ 0';
    }
    if (maxRaw === '' || maxRaw == null || !Number.isInteger(max) || max < 1) {
      errors['meta:maxSelection'] = 'Max selection must be a whole number ≥ 1';
    }
    if (Number.isInteger(min) && Number.isInteger(max) && min > max) {
      errors['meta:maxSelection'] = 'Max selection must be greater than or equal to min selection';
    }
    if (required && Number.isInteger(min) && min < 1) {
      errors['meta:minSelection'] = 'Required groups must have min selection of at least 1';
    }
  }

  if (schema?.type === 'experience_levels') {
    const minRaw = meta.minYears;
    const maxRaw = meta.maxYears;
    const min = Number(minRaw);
    const max = Number(maxRaw);
    const minValid = minRaw !== '' && minRaw != null && Number.isInteger(min) && min >= 0 && min <= 50;
    const maxValid = maxRaw !== '' && maxRaw != null && Number.isInteger(max) && max >= 0 && max <= 50;
    if (!minValid) errors['meta:minYears'] = 'Min years must be a whole number between 0 and 50';
    if (!maxValid) errors['meta:maxYears'] = 'Max years must be a whole number between 0 and 50';
    if (minValid && maxValid && min > max) {
      errors['meta:minYears'] = 'Min years cannot be greater than max years';
      errors['meta:maxYears'] = 'Max years must be greater than or equal to min years';
    }
  }

  if (schema?.type === 'restaurant_opening_hours' && !isOpeningHourClosed(meta)) {
    const open = String(meta.openTime || '');
    const close = String(meta.closeTime || '');
    if (!open.trim()) {
      errors['meta:openTime'] = 'Opening time is required';
    } else if (!TIME_PATTERN.test(open)) {
      errors['meta:openTime'] = 'Use 24-hour format HH:mm (e.g. 10:00, 22:30)';
    }
    if (!close.trim()) {
      errors['meta:closeTime'] = 'Closing time is required';
    } else if (!TIME_PATTERN.test(close)) {
      errors['meta:closeTime'] = 'Use 24-hour format HH:mm (e.g. 10:00, 22:30)';
    }
    if (open && close && TIME_PATTERN.test(open) && TIME_PATTERN.test(close) && open >= close) {
      errors['meta:closeTime'] = 'Closing time must be after opening time';
    }
    const dayErr = validateDuplicateOpeningHourDay(
      String(meta.day ?? ''),
      opts?.openingHourRecords || [],
      opts?.editingId,
    );
    if (dayErr) errors['meta:day'] = dayErr;
  }

  if (schema?.type === 'restaurant_opening_hours' && isOpeningHourClosed(meta)) {
    const dayErr = validateDuplicateOpeningHourDay(
      String(meta.day ?? ''),
      opts?.openingHourRecords || [],
      opts?.editingId,
    );
    if (dayErr) errors['meta:day'] = dayErr;
  }

  if (schema?.type === 'food_availability_schedules' || schema?.type === 'service_availability_schedules') {
    const start = String(meta.startTime || '');
    const end = String(meta.endTime || '');
    if (!start.trim()) {
      errors['meta:startTime'] = 'Start time is required';
    } else if (!TIME_PATTERN.test(start)) {
      errors['meta:startTime'] = 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)';
    }
    if (!end.trim()) {
      errors['meta:endTime'] = 'End time is required';
    } else if (!TIME_PATTERN.test(end)) {
      errors['meta:endTime'] = 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)';
    }
    if (
      start && end
      && TIME_PATTERN.test(start)
      && TIME_PATTERN.test(end)
      && start >= end
    ) {
      errors['meta:endTime'] = 'End time must be after start time';
    }
  }

  if (schema?.type === 'delivery_slots' || masterType === 'delivery_slots') {
    const start = String(meta.start || meta.startTime || '');
    const end = String(meta.end || meta.endTime || '');
    if (!start.trim()) {
      errors['meta:start'] = 'Start time is required';
    } else if (!TIME_PATTERN.test(start)) {
      errors['meta:start'] = 'Use 24-hour format HH:mm (e.g. 07:00, 18:00)';
    }
    if (!end.trim()) {
      errors['meta:end'] = 'End time is required';
    } else if (!TIME_PATTERN.test(end)) {
      errors['meta:end'] = 'Use 24-hour format HH:mm (e.g. 10:00, 22:00)';
    }
    if (
      start && end
      && TIME_PATTERN.test(start)
      && TIME_PATTERN.test(end)
      && start >= end
    ) {
      errors['meta:end'] = 'End time must be after start time';
    }
    const feeErr = validateDeliverySlotFee(meta.fee);
    if (feeErr) errors['meta:fee'] = feeErr;
  }

  if (schema?.type === 'packaging_types' || masterType === 'packaging_types') {
    const imageUrl = String(meta.imageUrl ?? '').trim();
    if (!imageUrl) {
      errors['meta:imageUrl'] = 'Packaging image is required';
    }
    const feeErr = validatePackagingFee(meta.fee);
    if (feeErr) errors['meta:fee'] = feeErr;
  }

  if (schema?.type === 'delivery_partner_pricing' || masterType === 'delivery_partner_pricing') {
    const zoneServiceErr = validateDuplicateDeliveryPartnerPricingZoneService(
      meta.zoneCode,
      meta.serviceSlug,
      opts?.existingRecords || [],
      opts?.editingId,
    );
    if (zoneServiceErr) errors['meta:zoneCode'] = zoneServiceErr;

    const pricingErrors = validateDeliveryPartnerPricingFields(meta);
    for (const [key, message] of Object.entries(pricingErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'delivery_incentives' || masterType === 'delivery_incentives') {
    const amountErr = validateDeliveryIncentiveAmount(meta.amount);
    if (amountErr) errors['meta:amount'] = amountErr;
    const peakErrors = validateDeliveryIncentivePeakTimeRange(meta);
    for (const [key, message] of Object.entries(peakErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'delivery_partners_tiers' || masterType === 'delivery_partners_tiers') {
    const minOrdersErr = validateDeliveryPartnerTierMinOrders(meta.minOrders);
    if (minOrdersErr) errors['meta:minOrders'] = minOrdersErr;
    else {
      const dupMin = validateDuplicateDeliveryPartnerTierMinOrders(
        meta.minOrders,
        opts?.existingRecords || [],
        opts?.editingId,
      );
      if (dupMin) errors['meta:minOrders'] = dupMin;
      else {
        const sequenceErr = validateDeliveryPartnerTierSequence(
          meta.minOrders,
          form.sortOrder,
          opts?.existingRecords || [],
          opts?.editingId,
        );
        if (sequenceErr) errors['meta:minOrders'] = sequenceErr;
      }
    }
    const bonusErr = validateDeliveryPartnerTierBonusPercent(meta.bonusPercent);
    if (bonusErr) errors['meta:bonusPercent'] = bonusErr;
  }

  if (schema?.type === 'merchant_document_types' || masterType === 'merchant_document_types') {
    const docErrors = validateMerchantDocumentFields(meta);
    for (const [key, message] of Object.entries(docErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'cancellation_reasons' || masterType === 'cancellation_reasons') {
    const cancelErrors = validateCancellationReasonFields(meta);
    for (const [key, message] of Object.entries(cancelErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'substitution_rules' || masterType === 'substitution_rules') {
    const subErrors = validateSubstitutionRuleFields(meta);
    for (const [key, message] of Object.entries(subErrors)) {
      errors[`meta:${key}`] = message;
    }
  }

  if (schema?.type === 'service_availability_schedules' || schema?.type === 'food_availability_schedules') {
    const days = parseCommaSeparatedDayValues(form.metadata.daysOfWeek);
    if (!days.length) {
      errors['meta:daysOfWeek'] = 'Select at least one day';
    } else if (schema?.type === 'food_availability_schedules') {
      const weekendNameErr = validateFoodAvailabilityScheduleName(form.name, String(form.metadata.daysOfWeek ?? ''));
      if (weekendNameErr) errors.name = weekendNameErr;
    }
  }

  if (schema?.type === 'restaurant_branches') {
    const branchType = String(form.metadata.branchType ?? '');
    const restaurantSlug = String(form.metadata.restaurantSlug ?? '');
    const branchErr = validateRestaurantBranchType(
      branchType,
      restaurantSlug,
      opts?.branchRecords || [],
      opts?.editingId,
    );
    if (branchErr) errors['meta:branchType'] = branchErr;
  }

  return errors;
}

export function isMasterFormValid(errors: FieldErrors): boolean {
  return Object.keys(errors).length === 0;
}

export function summarizeMasterFormErrors(
  errors: FieldErrors,
  schema?: MasterSchema | null,
): string[] {
  const labelForKey = (key: string): string => {
    if (key.startsWith('meta:')) {
      const metaKey = key.slice(5);
      const field = schema?.metadataFields?.find((f) => f.key === metaKey);
      return field?.label || metaKey;
    }
    const defaults: Record<string, string> = {
      code: 'Code',
      name: 'Display name',
      sortOrder: 'Sort order',
      parentId: 'Parent master',
      description: 'Description',
    };
    return defaults[key] || key;
  };

  return Object.entries(errors).map(([key, message]) => `${labelForKey(key)}: ${message}`);
}

export function isDuplicateMasterCode(
  code: string,
  records: Array<{ _id?: string; code?: string }>,
  excludeId?: string,
): boolean {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return false;
  return records.some((row) => {
    if (excludeId && row._id === excludeId) return false;
    return String(row.code || '').trim().toUpperCase() === normalized;
  });
}

export function validateDuplicateMasterCode(
  code: string,
  records: Array<{ _id?: string; code?: string }>,
  excludeId?: string,
  label = 'Code',
): string | undefined {
  if (!code.trim()) return undefined;
  if (isDuplicateMasterCode(code, records, excludeId)) {
    return `${label} is already used by another record`;
  }
  return undefined;
}

export function validateDuplicateSkillName(
  name: string,
  records: Array<{ _id?: string; name?: string; code?: string }> = [],
  excludeId?: string,
): string | undefined {
  const normalized = String(name || '').replace(/\[deprecated\]/ig, '').trim().toLowerCase();
  if (!normalized) return undefined;
  const duplicate = records.find((row) => {
    if (excludeId && row._id === excludeId) return false;
    const rowName = String(row.name || '').replace(/\[deprecated\]/ig, '').trim().toLowerCase();
    return rowName === normalized;
  });
  if (duplicate) {
    return `Skill name is already used by code "${duplicate.code || 'another record'}" — names must be unique`;
  }
  return undefined;
}

export function isDuplicateSortOrder(
  sortOrder: number,
  records: Array<{ _id?: string; sortOrder?: number; metadata?: Record<string, unknown> }> = [],
  excludeId?: string,
): boolean {
  if (!Number.isFinite(sortOrder) || !Number.isInteger(sortOrder) || sortOrder < 1) return false;
  return records.some((row) => {
    if (excludeId && row._id === excludeId) return false;
    const existing = resolveRecordSortOrder(row);
    return existing >= 1 && existing === sortOrder;
  });
}

export function validateDuplicateSortOrder(
  sortOrder: number,
  records: Array<{ _id?: string; sortOrder?: number; metadata?: Record<string, unknown> }> = [],
  excludeId?: string,
  label = 'Sort order',
): string | undefined {
  if (isDuplicateSortOrder(sortOrder, records, excludeId)) {
    return `${label} is already used by another record — choose the next available number`;
  }
  return undefined;
}
